/**
 * perfProbe.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * مقياس تأخّر الاستجابة — يجيب عن سؤال واحد: **ما الذي يحجز الخيط الرئيسي حين
 * تضغط زرّاً أو تسحب الشريط؟**
 *
 * لماذا هذا القياس تحديداً؟
 * ------------------------
 * «الأزرار بطيئة» ليست حالة رسم بل حالة **تأخّر إدخال**. المتصفّح لا يستطيع تنفيذ
 * معالِج النقر إلا بعد أن يفرغ الخيط الرئيسي؛ فإن كانت هناك مهمّة تستغرق 120 مللي
 * (فكّ راستر مثلاً) وضغطتَ في أثنائها، فأقصر زمن ممكن للاستجابة هو ما تبقّى منها.
 * لذلك المقياس الصحيح ليس FPS بل:
 *
 *   ❶ Long Tasks — كل مهمّة تتجاوز 50 مللي على الخيط الرئيسي (تعريف W3C). هذه هي
 *     «الحُفَر» التي تبتلع النقرات. نعرض عددها ومجموعها وأطولها.
 *   ❷ Event Timing — الزمن الفعلي من لمس الإصبع حتى رسم النتيجة لكل تفاعل
 *     (نفس ما يُبنى عليه مقياس INP في Core Web Vitals). هذا **قياس مباشر** لما
 *     تشكو منه، لا استدلال عليه.
 *   ❸ أقسام موسومة — نلفّ المواضع الساخنة المعروفة بـ`perfSection` فنعرف أي كود
 *     يقع داخل تلك الحُفَر بدل أن نخمّن.
 *
 * التشغيل: أضف `?perf=1` إلى الرابط، أو نفّذ في الكونسول
 * `localStorage.setItem('skyradar:perf','1')` ثم أعد التحميل. تظهر لوحة صغيرة أعلى
 * اليسار، وتُطبع حصيلة كل ثانيتين في الكونسول أيضاً.
 *
 * الكلفة حين يكون مُطفأً: مقارنة قيمة منطقية واحدة عند كل نداء `perfSection`
 * (‏`if (!enabled) return fn()`) — أي لا شيء يُذكر، فلا داعي لنزعه بعد التشخيص.
 */

interface SectionStat {
    /** عدد الاستدعاءات منذ آخر تفريغ. */
    calls: number;
    /** مجموع الزمن بالمللي منذ آخر تفريغ. */
    total: number;
    /** أطول استدعاء منفرد منذ آخر تفريغ. */
    max: number;
}

const sections = new Map<string, SectionStat>();

let enabled = false;
let longTaskCount = 0;
let longTaskTotal = 0;
let longTaskMax = 0;
let worstEvent = { name: '', duration: 0 };
let eventCount = 0;
let eventTotal = 0;
let frames = 0;
let windowStart = 0;

/** هل القياس مفعّل؟ يُقرأ مرّة واحدة عند الإقلاع. */
export function perfEnabled(): boolean {
    return enabled;
}

/**
 * يقيس زمن `fn` وينسبه إلى `name`. يُعيد ما تُعيده `fn` بلا تغيير في السلوك.
 * مُصمَّم ليُلفّ حول الأعمال المتزامنة الثقيلة فقط — لا حول دوال تُستدعى ملايين المرّات.
 */
export function perfSection<T>(name: string, fn: () => T): T {
    if (!enabled) return fn();
    const t0 = performance.now();
    try {
        return fn();
    } finally {
        const dt = performance.now() - t0;
        let s = sections.get(name);
        if (!s) { s = { calls: 0, total: 0, max: 0 }; sections.set(name, s); }
        s.calls += 1;
        s.total += dt;
        if (dt > s.max) s.max = dt;
    }
}

/** عدّاد بسيط بلا توقيت — لعدّ الأحداث (إعادات رسم React مثلاً). */
export function perfCount(name: string): void {
    if (!enabled) return;
    let s = sections.get(name);
    if (!s) { s = { calls: 0, total: 0, max: 0 }; sections.set(name, s); }
    s.calls += 1;
}

function fmt(n: number): string {
    return n < 10 ? n.toFixed(1) : String(Math.round(n));
}

function buildReport(elapsed: number): string {
    const fps = elapsed > 0 ? (frames * 1000) / elapsed : 0;
    const lines: string[] = [];
    lines.push(`FPS ${fps.toFixed(0)}`);
    lines.push(`حُفَر >50ms: ${longTaskCount}  مجموع ${fmt(longTaskTotal)}ms  أطول ${fmt(longTaskMax)}ms`);
    if (eventCount > 0) {
        lines.push(`تفاعلات: ${eventCount}  متوسّط ${fmt(eventTotal / eventCount)}ms  أسوأ ${worstEvent.name} ${fmt(worstEvent.duration)}ms`);
    }
    const rows = [...sections.entries()]
        .sort((a, b) => (b[1].total - a[1].total) || (b[1].calls - a[1].calls))
        .slice(0, 8);
    for (const [name, s] of rows) {
        lines.push(s.total > 0
            ? `  ${name}: ${s.calls}×  ${fmt(s.total)}ms  (أطول ${fmt(s.max)})`
            : `  ${name}: ${s.calls}×`);
    }
    return lines.join('\n');
}

function reset(): void {
    sections.clear();
    longTaskCount = 0; longTaskTotal = 0; longTaskMax = 0;
    eventCount = 0; eventTotal = 0; worstEvent = { name: '', duration: 0 };
    frames = 0;
    windowStart = performance.now();
}

/**
 * يبدأ القياس إن كان مطلوباً. يُستدعى مرّة عند إقلاع التطبيق؛ النداء الثاني بلا أثر.
 */
export function initPerfProbe(): void {
    if (typeof window === 'undefined' || enabled) return;
    const wanted = new URLSearchParams(window.location.search).get('perf') === '1'
        || window.localStorage?.getItem('skyradar:perf') === '1';
    if (!wanted) return;
    enabled = true;
    windowStart = performance.now();

    // ❶ المهام الطويلة: مصدر تأخّر الإدخال المباشر.
    try {
        new PerformanceObserver((list) => {
            for (const e of list.getEntries()) {
                longTaskCount += 1;
                longTaskTotal += e.duration;
                if (e.duration > longTaskMax) longTaskMax = e.duration;
            }
        }).observe({ type: 'longtask', buffered: true });
    } catch { /* متصفّح بلا longtask */ }

    // ❷ زمن التفاعل الحقيقي: من الضغط حتى الرسم.
    try {
        new PerformanceObserver((list) => {
            for (const e of list.getEntries()) {
                eventCount += 1;
                eventTotal += e.duration;
                if (e.duration > worstEvent.duration) worstEvent = { name: e.name, duration: e.duration };
            }
        }).observe({ type: 'event', durationThreshold: 16, buffered: true } as PerformanceObserverInit);
    } catch { /* متصفّح بلا event timing */ }

    // ❸ عدّاد الإطارات — للتمييز بين «الرسم بطيء» و«الخيط محجوز».
    const tick = () => { frames += 1; requestAnimationFrame(tick); };
    requestAnimationFrame(tick);

    const box = document.createElement('div');
    box.setAttribute('dir', 'ltr');
    box.style.cssText = 'position:fixed;top:8px;left:8px;z-index:99999;background:rgba(8,10,16,.86);'
        + 'color:#d8f5c8;font:11px/1.45 ui-monospace,Consolas,monospace;padding:8px 10px;border-radius:6px;'
        + 'white-space:pre;pointer-events:none;max-width:46ch;border:1px solid rgba(255,255,255,.14)';
    document.body.appendChild(box);

    window.setInterval(() => {
        const elapsed = performance.now() - windowStart;
        const report = buildReport(elapsed);
        box.textContent = report;
        // نسخة في الكونسول كي يمكن نسخها ولصقها كاملةً.
        console.info('[perf]\n' + report);
        reset();
    }, 2000);
}
